import { useMemo, useState } from "react";

// mui icons
import CloseIcon from "@mui/icons-material/Close";

// mui components
import { Stack, InputLabel, Box } from "@mui/material";

// custom components
import Heading3 from "../../../typography/heading-03";
import Heading6 from "../../../typography/heading-06";
import SecondaryButton from "../../../buttons/secondary-button";
import PrimaryButton from "../../../buttons/primary-button";
import OutlinedTextInput from "../../../custom-input/outlined-text-input";
import OutlinedTextarea from "../../../custom-input/outlined-textarea";
import SecondarySelect from "../../../custom-select/secondary-select";
import CustomCheckbox from "../../../custom-checkbox";
import DatePicker from "react-datepicker";
import { DateTime } from "luxon";

// api slice
import { useCreateMessageMutation } from "../../../../features/messages/messagesApiSlice";

// helper utils
import {
	MESSAGE_ROLES,
	MESSAGE_TYPES,
} from "../../../../utils/messages.constants";
import { messageTypeOptions } from "../../../../utils/select-options";
import { rolesToString } from "../../../../utils/role-transformer";

// custom styles
import styles from "./index.module.scss";
import "react-datepicker/dist/react-datepicker.css";
import "../request-closing-date/styles.css";

const AddMessageDrawer = ({
	handleCloseDrawer,
	handleOpenProcessingModal,
	handleCloseProcessingModal,
	handleOpenSuccessModal,
	setProcessingText,
	setSuccessText,
	handleOpenCommonErrorModal,
	toggleDrawer,
	drawerData,
	drawerType,
	refreshMessages,
}) => {
	const [createMessage, { isLoading: isCreatingMessage }] =
		useCreateMessageMutation();

	const [subject, setSubject] = useState("");
	const [details, setDetails] = useState("");
	const [category, setCategory] = useState("");
	const [selectedRoles, setSelectedRoles] = useState([]);
	const [startAt, setStartAt] = useState(null);
	const [endAt, setEndAt] = useState(null);

	const resetForm = () => {
		setSubject("");
		setDetails("");
		setCategory("");
		setSelectedRoles([]);
		setStartAt(null);
		setEndAt(null);
		handleCloseDrawer();
	};

	const getCurrentUtcAsLocalDate = () => {
		const nowUtc = DateTime.now().setZone("utc");
		return new Date(
			nowUtc.year,
			nowUtc.month - 1,
			nowUtc.day,
			nowUtc.hour,
			nowUtc.minute,
			nowUtc.second
		);
	};

	const minStartDate = useMemo(() => getCurrentUtcAsLocalDate(), []);

	const getMinEndDate = useMemo(() => {
		return startAt ? startAt : getCurrentUtcAsLocalDate();
	}, [startAt]);

	const getMinTime = (date) => {
		const nowUtc = getCurrentUtcAsLocalDate();
		if (!date) return new Date(0, 0, 0, 0, 0, 0);

		const selectedDateLocal = DateTime.fromJSDate(date);
		const nowUtcLocal = DateTime.fromJSDate(nowUtc);
		const isToday = selectedDateLocal.hasSame(nowUtcLocal, "day");

		return isToday ? nowUtc : new Date(0, 0, 0, 0, 0, 0);
	};

	const getMaxTime = () => new Date(0, 0, 0, 23, 59, 59);

	const handleSubmit = async () => {
		// Validations
		if (!subject.trim()) {
			handleOpenCommonErrorModal("ADMIN_SUBJECT_REQUIRED");
			return;
		}

		if (!details.trim()) {
			handleOpenCommonErrorModal("ADMIN_DETAILS_REQUIRED");
			return;
		}

		if (!category) {
			handleOpenCommonErrorModal("ADMIN_CATEGORY_REQUIRED");
			return;
		}

		if (!startAt) {
			handleOpenCommonErrorModal("ADMIN_START_DATETIME_REQUIRED");
			return;
		}

		if (!endAt) {
			handleOpenCommonErrorModal("ADMIN_END_DATETIME_REQUIRED");
			return;
		}

		// date validations
		const nowUtc = getCurrentUtcAsLocalDate();
		if (DateTime.fromJSDate(startAt) < DateTime.fromJSDate(nowUtc)) {
			handleOpenCommonErrorModal("ADMIN_START_DATETIME_INVALID");
			return;
		}
		if (DateTime.fromJSDate(endAt) < DateTime.fromJSDate(startAt)) {
			handleOpenCommonErrorModal("ADMIN_END_DATETIME_INVALID");
			return;
		}

		if (
			category === MESSAGE_TYPES.ROLE_SPECIFIC &&
			selectedRoles.length === 0
		) {
			handleOpenCommonErrorModal("ADMIN_ROLE_REQUIRED");
			return;
		}

		try {
			setProcessingText("Adding message...");
			handleOpenProcessingModal();

			let formData = new FormData();
			formData.append("title", subject);
			formData.append("body", details);
			formData.append("message_type", category);

			if (category === MESSAGE_TYPES.ROLE_SPECIFIC) {
				formData.append("role", rolesToString(selectedRoles));
			} else if (category === MESSAGE_TYPES.GENERAL) {
				formData.append("role", rolesToString(MESSAGE_ROLES));
			}

			// Convert the local Date object back to UTC ISO string for submission
			const startFormatted = DateTime.fromObject(
				{
					year: startAt.getFullYear(),
					month: startAt.getMonth() + 1,
					day: startAt.getDate(),
					hour: startAt.getHours(),
					minute: startAt.getMinutes(),
					second: startAt.getSeconds(),
				},
				{ zone: "utc" }
			).toISO();

			const endFormatted = DateTime.fromObject(
				{
					year: endAt.getFullYear(),
					month: endAt.getMonth() + 1,
					day: endAt.getDate(),
					hour: endAt.getHours(),
					minute: endAt.getMinutes(),
					second: endAt.getSeconds(),
				},
				{ zone: "utc" }
			).toISO();
			formData.append("start_date", startFormatted);
			formData.append("end_date", endFormatted);

			let response = await createMessage(formData).unwrap();

			handleCloseProcessingModal();

			if (response && response?.id) {
				setSuccessText("Message created successfully");
				handleOpenSuccessModal();

				if (refreshMessages) {
					refreshMessages();
				}
				resetForm();
			} else {
				handleOpenCommonErrorModal("ADMIN_MESSAGE_NOT_CREATED");
			}
		} catch (err) {
			handleCloseProcessingModal();
			handleOpenCommonErrorModal("ADMIN_MESSAGE_NOT_CREATED_API");
		}
	};

	return (
		<Stack direction="column" className={styles.stackContainer}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				className={styles.drawerHeaderContainer}
			>
				<Heading3
					text="Add new message"
					extraClass={styles.headerText}
					fontType="semibold"
				/>
				<Stack direction="row" gap={2} alignItems="center">
					<SecondaryButton
						startIcon={<CloseIcon />}
						text="Close"
						onClick={handleCloseDrawer}
					/>
				</Stack>
			</Stack>

			<Stack direction="column" className={styles.drawerBodyContainer}>
				{/* Subject */}
				<Stack direction="column" className={styles.inputContainer}>
					<InputLabel htmlFor="subject-input">
						<Heading6
							text="Subject"
							fontType="semibold"
							extraClass={styles.inputHeader}
						/>
					</InputLabel>
					<OutlinedTextInput
						id="subject-input"
						autoComplete="off"
						placeholder="Enter Subject"
						value={subject}
						handleChange={(event) => setSubject(event.target.value)}
						fullWidth={true}
					/>
				</Stack>

				{/* Details */}
				<Stack direction="column" className={styles.inputContainer}>
					<InputLabel htmlFor="details-input">
						<Heading6
							text="Details"
							fontType="semibold"
							extraClass={styles.inputHeader}
						/>
					</InputLabel>
					<OutlinedTextarea
						id="details-input"
						autoComplete="off"
						placeholder="Enter Details"
						value={details}
						handleChange={(event) => setDetails(event.target.value)}
						fullWidth={true}
						rows={4}
					/>
				</Stack>

				{/* Category */}
				<Stack direction="column" className={styles.inputContainer}>
					<InputLabel htmlFor="category-select">
						<Heading6
							text="Category"
							fontType="semibold"
							extraClass={styles.inputHeader}
						/>
					</InputLabel>
					<SecondarySelect
						id="category-select"
						displayEmpty={true}
						placeholder="Select"
						fullWidth={true}
						value={category}
						handleChange={(event) => setCategory(event.target.value)}
						options={messageTypeOptions}
					/>
				</Stack>

				{/* Role selection (only when Role Specific) */}
				{category === MESSAGE_TYPES.ROLE_SPECIFIC && (
					<Stack direction="column" className={styles.rolesContainer}>
						<Heading6 text="Select Role(s):" fontType="semibold" />
						<Box className={styles.rolesGrid}>
							{MESSAGE_ROLES.map((role) => (
								<CustomCheckbox
									key={role}
									label={role}
									checked={selectedRoles.includes(role)}
									onChange={() => {
										setSelectedRoles((prev) =>
											prev.includes(role)
												? prev.filter((r) => r !== role)
												: [...prev, role]
										);
									}}
								/>
							))}
						</Box>
					</Stack>
				)}

				{/* Date/Time inputs */}
				<Stack direction="row" className={styles.dateTimeContainer}>
					{/* Start Date/Time */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor="start-datetime-picker">
							<Heading6
								text="Start DateTime (UTC)"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>
						<DatePicker
							id="start-datetime-picker"
							autoComplete="off"
							className={styles.datepicker}
							selected={startAt}
							onChange={(date) => {
								setStartAt(date);
								if (
									endAt &&
									date &&
									DateTime.fromJSDate(endAt) < DateTime.fromJSDate(date)
								) {
									setEndAt(date);
								}
							}}
							showTimeSelect
							timeIntervals={15}
							timeFormat="HH:mm"
							dateFormat="LLL dd yyyy, HH:mm 'UTC'"
							minDate={minStartDate}
							minTime={getMinTime(startAt || getCurrentUtcAsLocalDate())}
							maxTime={getMaxTime()}
							placeholderText="Select start date/time"
						/>
					</Stack>

					{/* End Date/Time */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor="end-datetime-picker">
							<Heading6
								text="End DateTime (UTC)"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>
						<DatePicker
							id="end-datetime-picker"
							autoComplete="off"
							className={styles.datepicker}
							selected={endAt}
							onChange={(date) => setEndAt(date)}
							showTimeSelect
							timeIntervals={15}
							timeFormat="HH:mm"
							dateFormat="LLL dd yyyy, HH:mm 'UTC'"
							minDate={getMinEndDate}
							minTime={getMinTime(
								endAt || startAt || getCurrentUtcAsLocalDate()
							)}
							maxTime={getMaxTime()}
							placeholderText="Select end date/time"
						/>
					</Stack>
				</Stack>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="flex-end"
					className={styles.drawerFooterContainer}
				>
					<SecondaryButton
						text="Cancel"
						onClick={resetForm}
						extraClass={styles.cancelBtn}
					/>
					<PrimaryButton
						text="Add Message"
						onClick={handleSubmit}
						disabled={isCreatingMessage}
					/>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default AddMessageDrawer;
